// 文学西湖 · 诗词地标数据获取
//
// 背景：原接口一次性拉全量（135 地标 + 8800+ 邻居 / 6.6MB）会在 nginx 504 超时。
// 根因：服务端无过滤时要遍历每个地标的全部邻居关系，超大地标（如孤山 1909 首）必然超时。
// 方案：
//   1. 先拿地标列表（不带邻居，秒级）
//   2. 阶段1：按地标并发拉邻居，每个带恒真过滤 neighborQueryString:'NOT dynasty:*'，
//      触发服务端索引快路径，普通地标秒级返回
//   3. 阶段2：对失败/空的地标（如孤山）串行重试，无并发抢占时可拿全
//   4. 合并成与原接口一致的 itemList，并缓存到 IndexedDB（数据约 6.6MB，超出 localStorage 上限）
//
// 对外仍导出 getPoetry，返回合并后的 itemList 数组，调用方无需改动。

import { sendRequest } from './api';
import { IGetData } from './interfaces';
import { PoetryLoadProgress } from '@/store/types';
import { idbGet, idbSet, isEnvelopeValid, clearCacheKey, CacheEnvelope } from './cache';

const ACTION = '/vlog-data-service/api/project/common/groot/entitySearch';

const POETRY_UID = '370257446254441477';
const POETRY_GRAPH = 'xihu_cw';
const POETRY_QUERY = 'tags:诗词地标 OR tags:诗词轮播';
const POETRY_INCLUDE = ['biz_info', 'tags', 'image', 'entity_id', 'name', 'summary', 'short_summary'];
const POETRY_NEIGHBOUR_PROPS = ['entity_id', 'name', 'summary', 'author', 'dynasty', 'tags', 'image'];
// 恒真过滤条件：dynasty:* 在本图谱匹配为空，故 NOT dynasty:* 恒真，
// 且能让服务端走索引查询、避免遍历全部邻居导致 60s 超时。
const POETRY_NEIGHBOUR_FILTER = 'NOT dynasty:*';
const POETRY_CONCURRENCY = 10;
const POETRY_RETRY = 5;

type PoetryParams = { activityId?: string; deviceId?: string };

type ProgressFn = (p: PoetryLoadProgress) => void;

const CACHE_KEY = 'poetry_neighbours';
// 缓存数据版本：数据结构变更或需强制刷新时 +1，旧缓存自动失效
const CACHE_VERSION = 1;
// 缓存有效期：超过则重新拉取（展厅每日开机即相当于每天刷新一次数据）
const CACHE_TTL_MS = 12 * 60 * 60 * 1000;

// ---------------- 请求 ----------------
// 简单并发池
async function runPool<T>(items: T[], concurrency: number, worker: (item: T) => Promise<void>) {
  let cursor = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (cursor < items.length) {
      const idx = cursor++;
      await worker(items[idx]);
    }
  });
  await Promise.all(runners);
}

// 只拉地标列表（不带邻居，秒级返回）
const fetchPoetryLandmarks = async (params: PoetryParams) => {
  const data: any = await sendRequest.request<IGetData>(ACTION, {
    ...params,
    activityId: 'test',
    uid: POETRY_UID,
    graph: POETRY_GRAPH,
    entitySearch: {
      label: 'addr',
      includeFields: POETRY_INCLUDE,
      queryString: POETRY_QUERY,
      size: 1000,
    },
  });
  return (data?.itemList || []) as any[];
};

// 拉单个地标的邻居（带恒真过滤触发索引快路径）
const fetchPoetryNeighbour = async (placeId: string, params: PoetryParams) => {
  const data: any = await sendRequest.request<IGetData>(ACTION, {
    ...params,
    activityId: 'test',
    uid: POETRY_UID,
    graph: POETRY_GRAPH,
    entitySearch: {
      label: 'addr',
      fetchNeighbour: true,
      includeFields: POETRY_INCLUDE,
      queryString: `_id:${placeId}`,
      size: 1,
      neighbourSearch: {
        neighborLabels: ['creative_work', 'ppl'],
        neighborQueryString: POETRY_NEIGHBOUR_FILTER,
        size: 4000,
        relNames: ['相关'],
        props: POETRY_NEIGHBOUR_PROPS,
      },
    },
  });
  return data?.itemList?.[0];
};

// 手动清除缓存（数据更新后想强制重新拉取时调用）
export const clearPoetryCache = () => clearCacheKey(CACHE_KEY);

// 渐进式（两阶段）写法：按地标并发拉 + 孤山串行重试 + IndexedDB 缓存
// onProgress 上报阶段与进度，供全局 loading 展示
// 返回合并后的 itemList（结构与原 getPoetry 一致）
export const getPoetryProgressive = async (params: PoetryParams, onProgress?: ProgressFn) => {
  const env = await idbGet<CacheEnvelope<any[]>>(CACHE_KEY);
  if (isEnvelopeValid(env, CACHE_VERSION, CACHE_TTL_MS)) {
    onProgress?.({ stage: 'done', current: 1, total: 1 });
    return env.data;
  }

  onProgress?.({ stage: 'list', current: 0, total: 0 });
  const landmarks = await fetchPoetryLandmarks(params);
  const total = landmarks.length;
  const merged = new Map<string, any>();
  const needRetry: string[] = [];
  let done = 0;

  // 阶段1：并发拉每个地标的邻居
  onProgress?.({ stage: 'neighbour', current: 0, total });
  await runPool(landmarks, POETRY_CONCURRENCY, async (lm) => {
    try {
      const item = await fetchPoetryNeighbour(lm.entity_id, params);
      if (item && (item.ext_neighborList?.length ?? 0) > 0) {
        merged.set(lm.entity_id, item);
      } else {
        needRetry.push(lm.entity_id);
      }
    } catch (e) {
      needRetry.push(lm.entity_id);
    }
    done += 1;
    onProgress?.({ stage: 'neighbour', current: done, total });
  });

  // 阶段2：对失败/空的地标串行重试（无并发抢占，超大地标如孤山可成）
  for (const id of needRetry) {
    onProgress?.({ stage: 'retry', current: merged.size, total });
    for (let i = 0; i < POETRY_RETRY; i++) {
      try {
        const item = await fetchPoetryNeighbour(id, params);
        if (item && (item.ext_neighborList?.length ?? 0) > 0) {
          merged.set(id, item);
          break;
        }
      } catch (e) {
        // 继续重试
      }
    }
  }

  // 合并：以地标列表为序，附加邻居；仍拿不到邻居的退回基础信息并补空邻居数组，
  // 保证每个地标对象都带 ext_neighborList（详情组件会直接 .filter，缺字段会崩）
  const result = landmarks.map((lm) => merged.get(lm.entity_id) || { ...lm, ext_neighborList: [] });

  const envelope: CacheEnvelope<any[]> = { version: CACHE_VERSION, timestamp: Date.now(), data: result };
  await idbSet(CACHE_KEY, envelope);

  onProgress?.({ stage: 'done', current: total, total });
  return result;
};

// 原始写法：一次性拉全量邻居（数据大、服务端常 504 超时）。
// 保留用于对比/回退，与渐进式返回结构一致，可互相替换。
export const getPoetryLegacy = async (params: PoetryParams) => {
  const data: any = await sendRequest.request<IGetData>(ACTION, {
    ...params,
    activityId: 'test',
    uid: POETRY_UID,
    graph: POETRY_GRAPH,
    entitySearch: {
      label: 'addr',
      fetchNeighbour: true,
      includeFields: ['biz_info', 'author', 'tags', 'dynasty', 'image', 'content', 'entity_id', 'name', 'summary', 'short_summary'],
      queryString: POETRY_QUERY,
      size: 1000,
      neighbourSearch: {
        neighborLabels: ['creative_work', 'ppl'],
        size: 4000,
        relNames: ['相关'],
        props: ['image', 'summary', 'author', 'dynasty', 'tags', 'biz_info'],
      },
    },
  });
  return data?.itemList;
};

// 切换开关：true=渐进式（默认，稳定不超时）；false=原始一次性写法
const USE_PROGRESSIVE = true;

// 对外统一入口：两种实现返回结构一致，可互相替换
// onProgress 仅渐进式有效（原始写法是一次性请求，无分段进度）
export const getPoetry = (params: PoetryParams, onProgress?: ProgressFn) =>
  USE_PROGRESSIVE ? getPoetryProgressive(params, onProgress) : getPoetryLegacy(params);
